/* eslint-disable arrow-body-style */
import { IndicatorAlertSettings } from "@core/domain/models/indicator-alert-settings.model";
import { Nullable } from "@core/domain/types/nullable.type";
import { GetMyIndicatorAlertSettingsUseCase } from "@core/domain/usecases/get-my-indicator-alert-settings.usecase";
import { UpdateMyIndicatorAlertSettingsUseCase } from "@core/domain/usecases/update-my-indicator-alert-settings.usecase";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import type { IndicatorSearchFilters } from "@project/data/dto/technical-proposal/technical-proposal.dto";
import {
    ActivitySlot,
    CreateIndicatorComment,
    Indicator,
    IndicatorComment,
    IndicatorGenderBreakdown,
    Objective,
} from "@project/domain/models/technical-proposals.model";
import { ExportIndicatorsByUseCase } from "@project/domain/usecases/monitoring/export-indicators-by.usecase";
import { CreateIndicatorCommentUseCase } from "@project/domain/usecases/technical-proposal/indicator/create-indicator-comment.usecase";
import { EditIndicatorUseCase } from "@project/domain/usecases/technical-proposal/indicator/edit-indicator.usecase";
import { GetAllIndicatorsByUseCase } from "@project/domain/usecases/technical-proposal/indicator/get-all-indicator-by.usecase";
import { GetIndicatorCommentsUseCase } from "@project/domain/usecases/technical-proposal/indicator/get-indicator-comments.usecase";
import { GetAllObjectivesByUseCase } from "@project/domain/usecases/technical-proposal/objective/get-all-objective-by.usecase";
import { inject, injectable } from "inversify";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

export type indicatorTable = Indicator & {
    [key: string]:
        | Nullable<string>
        | number
        | boolean
        | IndicatorGenderBreakdown
        | undefined;
};

export type ActivityIndicatorsData = {
    indicators: indicatorTable[];
    activityName: string;
    activityId: number;
};

export interface ActivitiesIndicators {
    [key: string]: ActivityIndicatorsData;
}

const getSlotFieldKey = (index: number): string => `slot[${index}]`;

const mapIndicatorTableToIndicator = (indicator: indicatorTable): Indicator => {
    const slots = (indicator.slots ?? []).map((slot, index) => {
        const slotValue = indicator[getSlotFieldKey(index)];

        return {
            name: slot.name,
            value:
                typeof slotValue === "string" || typeof slotValue === "number"
                    ? String(slotValue)
                    : "",
        };
    });

    return {
        achievedIndicator: indicator.achievedIndicator,
        activityId: indicator.activityId,
        calculationCriteria: indicator.calculationCriteria,
        description: indicator.description,
        frequency: indicator.frequency,
        frequencyLabel: indicator.frequencyLabel,
        id: indicator.id,
        indicatorType: indicator.indicatorType,
        indicatorTypeLabel: indicator.indicatorTypeLabel,
        purposeTypology: indicator.purposeTypology ?? null,
        purposeTypologyLabel: indicator.purposeTypologyLabel,
        estimatedIndicator: indicator.estimatedIndicator,
        name: indicator.name,
        disaggregateByGender: indicator.disaggregateByGender,
        estimatedGenderBreakdown: indicator.estimatedGenderBreakdown,
        achievedGenderBreakdown: indicator.achievedGenderBreakdown,
        derivedStatus: indicator.derivedStatus,
        derivedStatusCode: indicator.derivedStatusCode,
        targetConfig: indicator.targetConfig,
        responsibleEmployeeId: indicator.responsibleEmployeeId ?? undefined,
        responsibleEmployeeName: indicator.responsibleEmployeeName ?? undefined,
        slots,
        status: indicator.status,
        targetType: indicator.targetType,
        targetTypeLabel: indicator.targetTypeLabel,
        verificationFont: indicator.verificationFont,
    };
};

@injectable()
export class MonitoringTabViewModel extends BaseViewModel {
    _projectId: Nullable<number> = null;
    technicalProposalId: Nullable<number> = null;

    @observable
    initialLoading: boolean = true;

    @observable
    _indicators: Indicator[] = [];

    @observable
    objetives: Objective[] = [];

    @observable
    selectedObjetive: Objective | null = null;

    @observable
    commentsModalOpen: boolean = false;

    @observable
    commentsLoading: boolean = false;

    @observable
    commentsSaving: boolean = false;

    @observable
    alertSettingsModalOpen: boolean = false;

    @observable
    alertSettingsLoading: boolean = false;

    @observable
    alertSettingsSaving: boolean = false;

    @observable
    alertSettings: IndicatorAlertSettings = IndicatorAlertSettings.defaults();

    @observable
    selectedIndicator: Indicator | null = null;

    @observable
    indicatorComments: IndicatorComment[] = [];

    initialFiltersValue: IndicatorSearchFilters = {
        project: undefined,
        objective: undefined,
    };

    @observable
    filters: IndicatorSearchFilters = this.initialFiltersValue;

    @computed
    get activitiesIndicators(): ActivitiesIndicators {
        const indicatorsTable = this._indicators.reduce(
            (accIndicators, indicator) => {
                const activityId = indicator.activityId.toString();
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (!accIndicators[activityId]) {
                    accIndicators[activityId] = {
                        activityName: "",
                        activityId: indicator.activityId,
                        indicators: [],
                    };
                }

                const slots = indicator.slots?.reduce(
                    (acc, slot, i) => {
                        return {
                            ...acc,
                            [getSlotFieldKey(i)]: slot.value,
                        } as Record<string, ActivitySlot>;
                    },
                    {} as Record<string, ActivitySlot>,
                );

                const newIndicator = {
                    ...indicator,
                    ...slots,
                } as indicatorTable;

                accIndicators[activityId].indicators.push(newIndicator);
                accIndicators[activityId].activityName =
                    indicator.activityName ?? String(indicator.activityId);
                return accIndicators;
            },
            // eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter
            {} as ActivitiesIndicators,
        );

        return indicatorsTable;
    }

    constructor(
        @inject(GetAllIndicatorsByUseCase)
        private readonly getAllIndicatorsByUseCase: GetAllIndicatorsByUseCase,
        @inject(GetAllObjectivesByUseCase)
        private readonly getAllObjectivesByUseCase: GetAllObjectivesByUseCase,
        @inject(EditIndicatorUseCase)
        private readonly editIndicatorUseCase: EditIndicatorUseCase,
        @inject(GetIndicatorCommentsUseCase)
        private readonly getIndicatorCommentsUseCase: GetIndicatorCommentsUseCase,
        @inject(CreateIndicatorCommentUseCase)
        private readonly createIndicatorCommentUseCase: CreateIndicatorCommentUseCase,
        @inject(GetMyIndicatorAlertSettingsUseCase)
        private readonly getMyIndicatorAlertSettingsUseCase: GetMyIndicatorAlertSettingsUseCase,
        @inject(UpdateMyIndicatorAlertSettingsUseCase)
        private readonly updateMyIndicatorAlertSettingsUseCase: UpdateMyIndicatorAlertSettingsUseCase,
        @inject(ExportIndicatorsByUseCase)
        private readonly exportIndicatorsByUseCase: ExportIndicatorsByUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        this.initViewData();
    }

    async initViewData(): Promise<void> {
        await Promise.all([this.getAllIndicatorsBy(), this.getAllObjectives()]);

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    setFilters(filters: IndicatorSearchFilters): void {
        this.filters = filters;
        this.getAllIndicatorsBy();
    }

    async getAllObjectives(): Promise<void> {
        const objectives = await this.getAllObjectivesByUseCase.execute({
            project: this._projectId ?? undefined,
        });

        runInAction(() => {
            this.objetives = objectives;

            // Force to get technicalProposalId from the first objective
            this.technicalProposalId = objectives[0]?.technicalProposalId;
        });
    }

    async getAllIndicatorsBy(): Promise<void> {
        const indicators = await this.getAllIndicatorsByUseCase.execute({
            objective: this.filters.objective,
            project: this._projectId ?? undefined,
        });

        runInAction(() => {
            this._indicators = indicators;
        });
    }

    @action.bound
    async openCommentsModal(indicator: Indicator): Promise<void> {
        runInAction(() => {
            this.commentsModalOpen = true;
            this.commentsLoading = true;
            this.selectedIndicator = indicator;
            this.indicatorComments = [];
        });

        const comments = await this.getIndicatorCommentsUseCase.execute(
            indicator.id,
        );

        runInAction(() => {
            this.indicatorComments = comments;
            this.commentsLoading = false;
        });
    }

    @action.bound
    closeCommentsModal(): void {
        this.commentsModalOpen = false;
        this.commentsLoading = false;
        this.commentsSaving = false;
        this.selectedIndicator = null;
        this.indicatorComments = [];
    }

    @action
    setAlertSettingsModalOpen(open: boolean): void {
        this.alertSettingsModalOpen = open;
    }

    @action
    setAlertSettings(settings: IndicatorAlertSettings): void {
        this.alertSettings = settings;
    }

    async openAlertSettingsModal(): Promise<void> {
        this.setAlertSettingsModalOpen(true);
        await this.loadAlertSettings();
    }

    async loadAlertSettings(): Promise<void> {
        this.alertSettingsLoading = true;

        const result = await this.getMyIndicatorAlertSettingsUseCase.execute();

        runInAction(() => {
            this.alertSettingsLoading = false;
        });

        if (result.isLeft()) return;

        runInAction(() => {
            this.alertSettings = result.getOrThrow();
        });
    }

    async saveAlertSettings(): Promise<void> {
        this.alertSettingsSaving = true;

        const result = await this.updateMyIndicatorAlertSettingsUseCase.execute(
            this.alertSettings,
        );

        runInAction(() => {
            this.alertSettingsSaving = false;
        });

        if (result.isLeft()) return;

        runInAction(() => {
            this.alertSettings = result.getOrThrow();
        });

        ToastManagerStore.success();
        this.setAlertSettingsModalOpen(false);
    }

    @action.bound
    async createComment(comment: CreateIndicatorComment): Promise<void> {
        if (!this.selectedIndicator) return;

        const selectedIndicatorId = this.selectedIndicator.id;

        runInAction(() => {
            this.commentsSaving = true;
        });

        const createdComment = await this.createIndicatorCommentUseCase.execute(
            selectedIndicatorId,
            comment,
        );

        runInAction(() => {
            this.commentsSaving = false;
        });

        if (!createdComment) return;

        await this.getAllIndicatorsBy();

        runInAction(() => {
            this.indicatorComments = [
                createdComment,
                ...this.indicatorComments,
            ];

            this.selectedIndicator =
                this._indicators.find(
                    (indicator) => indicator.id === selectedIndicatorId,
                ) ?? this.selectedIndicator;
        });
    }

    @action.bound
    async saveIndicator(indicatorTable: indicatorTable): Promise<void> {
        const indicator = mapIndicatorTableToIndicator(indicatorTable);

        const updatedIndicator =
            await this.editIndicatorUseCase.execute(indicator);

        if (!updatedIndicator) {
            ToastManagerStore.error("Error saving indicator");
            return;
        }

        await this.getAllIndicatorsBy();
        ToastManagerStore.success();
    }

    async exportIndicatorsByActivity(
        technicalProposalId: number,
        name: string,
    ): Promise<void> {
        if (!technicalProposalId) return;

        LoadLayoutStore.start();

        const blob = await this.exportIndicatorsByUseCase.execute(
            technicalProposalId,
            this.filters,
        );

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${name}.xlsx`);
        document.body.appendChild(link);
        link.click();

        LoadLayoutStore.finish();
    }

    async exportIndicatorsByObjective(
        technicalProposalId: number,
        name: string,
    ): Promise<void> {
        if (!technicalProposalId) return;

        LoadLayoutStore.start();

        const blob = await this.exportIndicatorsByUseCase.execute(
            technicalProposalId,
            this.filters,
        );

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${name}.xlsx`);
        document.body.appendChild(link);
        link.click();

        LoadLayoutStore.finish();
    }
}
